import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  ReactNode,
  type JSX,
} from "react";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import {
  TextValidator,
  ValidatorComponentProps,
} from "react-material-ui-form-validator";
import { TextFieldProps } from "@mui/material/TextField";
import { Popper } from "@mui/material";
import { PopperProps } from "@mui/x-charts";

function CustomPopper(props: PopperProps) {
  const { anchorEl, style, ...other } = props;
  return (
    <Popper
      {...other}
      anchorEl={(anchorEl as HTMLElement).parentElement}
      placement="bottom-start"
      sx={{
        ...style,
        width: `${(anchorEl as HTMLElement)?.parentElement?.clientWidth || 100
          }px!important`,
      }}
    />
  );
}

type ValidatorAutocompleteProps<T> = Omit<
  AutocompleteProps<T, false, false, false, "div">,
  "value" | "onChange" | "renderInput"
> & {
  value: T | null;
  name: string;
  options: T[];
  // onChange expects a synthetic event with a target (with name and value)
  onChange: (event: { target: { name: string; value: T | null } }) => void;
  helperText?: ReactNode | string;
} & Pick<ValidatorComponentProps, "validators" | "errorMessages"> &
  Partial<Pick<TextFieldProps, "label" | "size" | "variant">>;

export interface ValidatorAutocompleteRef {
  validate: () => boolean;
}

function ValidatorAutocompleteInner<T>(
  props: ValidatorAutocompleteProps<T>,
  ref: React.Ref<ValidatorAutocompleteRef>
) {
  const {
    value,
    name,
    options,
    onChange,
    validators,
    errorMessages,
    size,
    label,
    helperText,
    ...rest
  } = props;
  const [internalValue, setInternalValue] = useState<any>(value || null);

  const handleValidation = useCallback(
    (val: any): boolean => {
      if (validators && errorMessages && validators.length > 0) {
        for (let i = 0; i < validators.length; i++) {
          const validator = validators[i];
          let valid = true;
          if (typeof validator === "string") {
            if (validator === "required") {
              valid = val !== null && val !== undefined && val !== "";
            }
            // Extend with more built-in validators as needed.
          } else if (typeof validator === "function") {
            valid = validator(val);
          }
          if (!valid) {
            return false;
          }
        }
      }
      return true;
    },
    [errorMessages, validators]
  );

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleChange = (_: any, newValue: any) => {
    setInternalValue(newValue);
    onChange({ target: { name, value: newValue } });
  };

  // Expose a validate method via ref
  useImperativeHandle(ref, () => ({
    validate: () => handleValidation(internalValue),
  }));

  return (
    <Autocomplete
      value={internalValue}
      options={options}
      onChange={handleChange}
      {...rest}
      PopperComponent={CustomPopper}
      renderInput={(params) => {
        const { InputProps, ...restParams } = params;
        const { ref: inputRef, ...inputPropsWithoutRef } = InputProps;

        return (
          <TextValidator
            {...restParams}
            size={size}
            inputRef={inputRef}
            label={label}
            InputProps={{ ...inputPropsWithoutRef }}
            name={name}
            value={internalValue || ""}
            validators={validators}
            errorMessages={errorMessages}
            helperText={helperText}
            fullWidth
            // onChange is handled by Autocomplete; provide a dummy.
            onChange={() => { }}
          />
        );
      }}
    />
  );
}

const ValidatorAutocomplete = forwardRef(ValidatorAutocompleteInner) as <T>(
  props: ValidatorAutocompleteProps<T> & {
    ref?: React.Ref<ValidatorAutocompleteRef>;
  }
) => JSX.Element;

export default ValidatorAutocomplete;
